/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useRef } from "react";
import { TouchableOpacity, View, ViewStyle } from "react-native";
import AgoraRTC, {
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from "agora-rtc-react";
import ButtonBase from "@material-ui/core/ButtonBase";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Fade from "@material-ui/core/Fade";

import Rodal from "rodal";
import "rodal/lib/rodal.css";
import Icon from "react-icons-kit";
import { ic_close } from "react-icons-kit/md/ic_close";
import { caretDown } from "react-icons-kit/fa/caretDown";

import { Text, styles } from "UpduoLibrary";
import { useTimerRef } from "UpduoLibrary/timers";
import {
  skaleBlack,
  skalePrimaryBlue,
  skaleTextBlue,
  skaleDeactiveGray,
  skaleWebTextGray,
  skaleWebBackgroundGray,
  skaleWebTextBackgroundLightGray,
  colorWithOpacity,
} from "config/UpduoColors";
import * as UpduoAnalytics from "config/UpduoAnalytics";

const dropdownItemHeight = 46;
const inputBorderStyle: ViewStyle = {
  borderRadius: 8,
  borderWidth: 1,
  borderColor: skaleWebBackgroundGray,
  borderStyle: "solid",
};
const deviceInputStyles: ViewStyle = {
  ...inputBorderStyle,
  backgroundColor: skaleWebTextBackgroundLightGray,
  height: dropdownItemHeight,
};
interface ChatSettingsModalProps {
  isVisible: boolean;
  onClose: Function;
  audioTrack: IMicrophoneAudioTrack | null;
  videoTrack: ICameraVideoTrack | null;
}

export default function ChatSettingsModal({
  isVisible,
  onClose,
  audioTrack,
  videoTrack,
}: ChatSettingsModalProps) {
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioVolume, setAudioVolume] = useState(0);
  const [audioDeviceMenuEl, setAudioDeviceMenuEl] = useState(null);
  const [videoDeviceMenuEl, setVideoDeviceMenuEl] = useState(null);
  const [audioDeviceId, setAudioDeviceId] = useState("");
  const [videoDeviceId, setVideoDeviceId] = useState("");

  const [setAudioVolumeTimer, clearAudioVolumeTimer] = useTimerRef();

  const audioTrackRef = useRef<IMicrophoneAudioTrack | null>();
  audioTrackRef.current = audioTrack;

  useEffect(() => {
    if (audioTrack) {
      const getAudioDevices = async () => {
        const retrievedAudioDevices = await AgoraRTC.getMicrophones();
        setAudioDevices(retrievedAudioDevices);
        const currentAudioDeviceId = retrievedAudioDevices.find(
          //@ts-ignore
          (device) => device.label === audioTrack?._deviceName
        )?.deviceId;
        setAudioDeviceId(String(currentAudioDeviceId));
      };
      getAudioDevices();
    }
  }, [audioTrack]);

  useEffect(() => {
    if (videoTrack) {
      const getVideoDevices = async () => {
        // Get all audio and video devices.
        const retrievedVideoDevices = await AgoraRTC.getCameras();
        setVideoDevices(retrievedVideoDevices);
        const currentVideoDeviceId = retrievedVideoDevices.find(
          //@ts-ignore
          (device) => device.label === videoTrack?._deviceName
        )?.deviceId;
        setVideoDeviceId(String(currentVideoDeviceId));
      };
      getVideoDevices();
    }
  }, [videoTrack]);

  useEffect(() => {
    if (isVisible) {
      setAudioVolumeTimer(
        setInterval(() => {
          const retrievedAudioVolume =
            audioTrackRef.current?.getVolumeLevel() || 0;
          setAudioVolume(retrievedAudioVolume);
        }, 100)
      );
    } else {
      clearAudioVolumeTimer();
    }
    return () => {
      clearAudioVolumeTimer();
    };
  }, [isVisible]);

  const _handleClickAudioDeviceMenu = (event) => {
    setAudioDeviceMenuEl(event.currentTarget);
  };

  const _handleAudioDeviceMenuClose = () => {
    setAudioDeviceMenuEl(null);
  };

  const _handleClickVideoDeviceMenu = (event) => {
    setVideoDeviceMenuEl(event.currentTarget);
  };

  const _handleVideoDeviceMenuClose = () => {
    setVideoDeviceMenuEl(null);
  };

  return (
    <Rodal
      visible={isVisible}
      onClose={() => onClose()}
      customStyles={{
        height: "fit-content",
        width: "fit-content",
        borderRadius: 12,
      }}
      customMaskStyles={{
        backgroundColor: colorWithOpacity(skaleDeactiveGray, 0.2),
      }}
    >
      <View
        style={{
          alignItems: "center",
          justifyContent: "center",
          margin: 20,
        }}
      >
        <View>
          <Text style={{ marginBottom: 10 }}>{"Camera:"}</Text>
          <View
            style={[
              styles.dropdown,
              deviceInputStyles,
              {
                height: dropdownItemHeight,
                margin: 0,
                padding: 0,
                alignItems: "flex-start",
                justifyContent: "center",
                overflow: "hidden",
                width: 500,
                marginBottom: 30,
              },
            ]}
          >
            <ButtonBase
              aria-controls={"fade-menu-video"}
              aria-haspopup={"true"}
              onClick={_handleClickVideoDeviceMenu}
              style={{
                alignItems: "center",
                width: "97%",
                marginLeft: "2%",
                color: skaleWebTextGray,
              }}
            >
              <Text
                style={{
                  display: "flex",
                  flex: 1,
                  alignItems: "flex-start",
                  color: skaleWebTextGray,
                  width: 350,
                  fontSize: 16,
                }}
              >
                {videoDevices &&
                videoDevices.length > 0 &&
                videoDevices.find((module) => module.deviceId === videoDeviceId)
                  ? videoDevices.find(
                      (module) => module.deviceId === videoDeviceId
                    )?.label
                  : ""}
              </Text>
              <Icon size={20} icon={caretDown} />
            </ButtonBase>
            <Menu
              id={"fade-menu-video"}
              anchorEl={videoDeviceMenuEl}
              open={Boolean(videoDeviceMenuEl)}
              onClose={_handleVideoDeviceMenuClose}
              TransitionComponent={Fade}
              style={{ marginLeft: -10.5, marginTop: 1 }}
            >
              {videoDevices.map((device) => (
                <MenuItem
                  key={device.deviceId}
                  value={device.deviceId}
                  style={{
                    color: skaleWebTextGray,
                    fontFamily: "ObjectSans-Regular",
                    width: 450,
                    height: dropdownItemHeight,
                    marginLeft: 0,
                    borderWidth: 1,
                  }}
                  onClick={() => {
                    UpduoAnalytics.logEvent("user-click-set-device", {
                      device,
                    });
                    videoTrack?.setDevice(device.deviceId);
                    setVideoDeviceId(device.deviceId);
                    _handleVideoDeviceMenuClose();
                  }}
                >
                  {device.label}
                </MenuItem>
              ))}
            </Menu>
          </View>
          <Text style={{ marginBottom: 10 }}>{"Microphone:"}</Text>
          <View
            style={[
              styles.dropdown,
              deviceInputStyles,
              {
                height: dropdownItemHeight,
                margin: 0,
                padding: 0,
                alignItems: "flex-start",
                justifyContent: "center",
                overflow: "hidden",
                width: 500,
              },
            ]}
          >
            <ButtonBase
              aria-controls={"fade-menu-audio"}
              aria-haspopup={"true"}
              onClick={_handleClickAudioDeviceMenu}
              style={{
                alignItems: "center",
                width: "97%",
                marginLeft: "2%",
                color: skaleWebTextGray,
              }}
            >
              <Text
                style={{
                  display: "flex",
                  flex: 1,
                  alignItems: "flex-start",
                  color: skaleWebTextGray,
                  width: 350,
                  fontSize: 16,
                }}
              >
                {audioDevices &&
                audioDevices.length > 0 &&
                audioDevices.find((module) => module.deviceId === audioDeviceId)
                  ? audioDevices.find(
                      (module) => module.deviceId === audioDeviceId
                    )?.label
                  : ""}
              </Text>
              <Icon size={20} icon={caretDown} />
            </ButtonBase>
            <Menu
              id={"fade-menu-audio"}
              anchorEl={audioDeviceMenuEl}
              open={Boolean(audioDeviceMenuEl)}
              onClose={_handleAudioDeviceMenuClose}
              TransitionComponent={Fade}
              style={{ marginLeft: -10.5, marginTop: 1 }}
            >
              {audioDevices.map((device) => (
                <MenuItem
                  key={device.deviceId}
                  value={device.deviceId}
                  style={{
                    color: skaleWebTextGray,
                    fontFamily: "ObjectSans-Regular",
                    width: 450,
                    height: dropdownItemHeight,
                    marginLeft: 0,
                    borderWidth: 1,
                  }}
                  onClick={() => {
                    UpduoAnalytics.logEvent("user-click-set-device", {
                      device,
                    });
                    audioTrack?.setDevice(device.deviceId);
                    setAudioDeviceId(device.deviceId);
                    _handleAudioDeviceMenuClose();
                  }}
                >
                  {device.label}
                </MenuItem>
              ))}
            </Menu>
          </View>
        </View>
        <Text style={{ marginBottom: 10, marginTop: 30 }}>
          {"Detected Microphone Level:"}
        </Text>
        <View
          style={{
            height: 10,
            width: 200,
            borderWidth: 1,
            overflow: "hidden",
            borderRadius: 5,
            borderColor: skaleBlack,
            alignItems: "flex-start",
          }}
        >
          <View
            style={{
              height: 10,
              width: 200 * audioVolume,
              backgroundColor: skalePrimaryBlue,
            }}
          />
        </View>
      </View>
      <View style={{ position: "absolute", top: 0, right: 0 }}>
        <TouchableOpacity
          onPress={() => {
            onClose();
          }}
          style={{
            paddingTop: 16,
            paddingRight: 16,
            alignSelf: "flex-end",
          }}
        >
          <div
            style={{
              color: skaleTextBlue,
              paddingBottom: 2.5,
            }}
          >
            <Icon size={25} icon={ic_close} />
          </div>
        </TouchableOpacity>
      </View>
    </Rodal>
  );
}
